"use client";

import { useEffect, useState } from "react";
import Loading from "../../../components/Loading/index.js";
import { Link, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { planPagos, roundUp } from "../../../utils/planPagos.js";
import TablePagos from "./table.js";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import { calcularDeudaRestante } from "./calcularDeudaRestante.js";
import { obtenerPagosRealizados } from "../../../utils/pagosRealizados.js";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

function PlanDePagos() {
  const { idInvestor, id, identity, idSimulator } = useParams();
  const [fechaPagoAnterior, setFechaPagoAnterior] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const { handleUpdate } = useUpdateDoc();
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const [investorRate, setInvestorRate] = useState(0); // Tasa de gana

  const [showDate, setshowDate] = useState(false);

  const { data: request, isFetching: loading } = useSnapshotWhere(
    `${idSimulator ? "simulator" : "requests"}`,
    {
      filter: {
        firstCondition: "uuid",
        secondCondition: "==",
        thirdCondition: id ? id : idSimulator,
      },
    }
  );

  const [searchParams] = useSearchParams();
  const investmentId = searchParams.get("i");

  const data = request[0];

  const { data: investorRes, isFetching: loadingInvestor } = useSnapshotWhere(
    `investor`,
    {
      filter: {
        firstCondition: "uuid",
        secondCondition: "==",
        thirdCondition: idInvestor,
      },
    }
  );
  const investorData = investorRes[0];

  useEffect(() => {
    setIsLoaded(true);
    setInvestorRate(data?.tasaInvestor || 0);
  }, [data]);

  // Datos simulados del inversionista (en producción vendrían de la base de datos)

  const investmentAmount = data?.ammount;

  if (loading || loadingInvestor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loading />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="w-20 h-20 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No hay datos disponibles
            </h3>
            <p className="text-gray-600">
              No se encontró información del crédito solicitado
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { schedule } = planPagos(data, investorRate);

  // Filtramos los pagos realizados hasta el momento
  const pagosRealizados = obtenerPagosRealizados(
    schedule,
    data?.paymentsInvestor
  );

  // Cálculos para el inversionista
  const monthlyInvestorEarning = schedule[0]?.cuotaAPagar.toFixed(0) || 0;
  const totalEarnedSoFar = monthlyInvestorEarning * pagosRealizados.length;
  const expectedTotalReturn = monthlyInvestorEarning * data.month;
  const progressPercentage = (
    (pagosRealizados.length / data.month) *
    100
  ).toFixed(1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleUpdateTasaInvestor = () => {
    handleUpdate("requests", data.uuid, {
      tasaInvestor: investorRate,
    });
    setShowInvestorModal(false);
  };

  const currentPayment = schedule[pagosRealizados.length - 1];

  const nextPayment = schedule[pagosRealizados.length] || schedule[0];

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div
          className={`transition-all duration-700 ease-out ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex flex-col lg:flex-row  lg:justify-between gap-6">
              <div className="space-y-2 w-[100%]">
                <div className="flex gap-3">
                  <div className="p-3 border-2 border-purple-600 rounded-xl flex items-center justify-center w-[70px]">
                    <svg
                      className="w-10 h-10 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-row items-center justify-between w-full">
                    <div className="">
                      <h1 className="text-3xl font-bold text-gray-900">
                        Plan de Pagos
                      </h1>
                      <Link to={`/plataforma/inversionista/${idInvestor}`}>
                        <div>
                          <p className="text-lg text-primary font-semibold hover:font-bold">
                            Inversionista {investorData.name}
                          </p>
                        </div>
                      </Link>
                    </div>
                    {data?.status === "pagado" ? (
                      <div className="bg-primary/20 rounded-lg p-2">
                        <p>Este crédito ya fue pagado</p>
                        <div>
                          {" "}
                          Monto: {formatCurrency(data?.montoPagoCompleto)}{" "}
                        </div>
                        <div> Fecha: {data?.fechaPagoCompleto} </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke="#1C274D"
                                strokeLinecap="round"
                                strokeWidth="1.5"
                                d="M21 12c0 4.714 0 7.071-1.318 8.535S16.242 22 12 22s-6.364 0-7.682-1.465C3 19.072 3 16.714 3 12s0-7.071 1.318-8.536S7.758 2 12 2s6.364 0 7.682 1.464c.876.974 1.17 2.343 1.268 4.536"
                              ></path>
                              <path
                                stroke="#1C274D"
                                strokeWidth="1.5"
                                d="M7 8c0-.465 0-.697.051-.888a1.5 1.5 0 0 1 1.06-1.06C8.304 6 8.536 6 9 6h6c.465 0 .697 0 .888.051a1.5 1.5 0 0 1 1.06 1.06C17 7.304 17 7.536 17 8s0 .697-.051.888a1.5 1.5 0 0 1-1.06 1.06C15.697 10 15.464 10 15 10H9c-.465 0-.697 0-.888-.051a1.5 1.5 0 0 1-1.06-1.06C7 8.696 7 8.464 7 8Z"
                              ></path>
                              <circle
                                cx="8"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="8"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="12"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="12"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="16"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="16"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                            </svg>
                          </div>
                          <h2 className="text-xl font-semibold text-gray-900">
                            Calculadora de Deuda Pendiente
                          </h2>
                        </div>
                        {!showDate ? (
                          <button
                            onClick={() => setshowDate(true)}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke="#ffffff"
                                strokeLinecap="round"
                                strokeWidth="1.5"
                                d="M21 12c0 4.714 0 7.071-1.318 8.535S16.242 22 12 22s-6.364 0-7.682-1.465C3 19.072 3 16.714 3 12s0-7.071 1.318-8.536S7.758 2 12 2s6.364 0 7.682 1.464c.876.974 1.17 2.343 1.268 4.536"
                              ></path>
                              <path
                                stroke="#ffffff"
                                strokeWidth="1.5"
                                d="M7 8c0-.465 0-.697.051-.888a1.5 1.5 0 0 1 1.06-1.06C8.304 6 8.536 6 9 6h6c.465 0 .697 0 .888.051a1.5 1.5 0 0 1 1.06 1.06C17 7.304 17 7.536 17 8s0 .697-.051.888a1.5 1.5 0 0 1-1.06 1.06C15.697 10 15.464 10 15 10H9c-.465 0-.697 0-.888-.051a1.5 1.5 0 0 1-1.06-1.06C7 8.696 7 8.464 7 8Z"
                              ></path>
                              <circle
                                cx="8"
                                cy="13"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                              <circle
                                cx="8"
                                cy="17"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                              <circle
                                cx="12"
                                cy="13"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                              <circle
                                cx="12"
                                cy="17"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                              <circle
                                cx="16"
                                cy="13"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                              <circle
                                cx="16"
                                cy="17"
                                r="1"
                                fill="#ffffff"
                              ></circle>
                            </svg>
                            Calcular Deuda Pendiente hasta la Fecha
                          </button>
                        ) : (
                          <div className="space-y-6 w-full">
                            <div className="grid grid-cols-1 gap-6 items-end">
                              <div className="space-y-2">
                                <label
                                  htmlFor="fechaPago"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Seleccionar fecha de cálculo
                                </label>
                                <div className="relative flex gap-3">
                                  <input
                                    id="fechaPago"
                                    type="date"
                                    value={fechaPagoAnterior}
                                    onChange={(e) => {
                                      setFechaPagoAnterior(e.target.value);
                                      calcularDeudaRestante(
                                        e.target.value,
                                        fechaPagoAnterior,
                                        currentPayment,
                                        Number(data.tasa)
                                      );
                                    }}
                                    className="  px-4 py-3 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                  />
                                  <button
                                    onClick={() => setshowDate(false)}
                                    className="px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200"
                                  >
                                    Ocultar Calculadora
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                                  <svg
                                    className="w-6 h-6 text-orange-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Deuda Restante
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    Calculada al{" "}
                                    {dayjs(fechaPagoAnterior).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="text-3xl font-bold text-orange-600">
                                {nextPayment
                                  ? formatCurrency(
                                      roundUp(
                                        calcularDeudaRestante(
                                          nextPayment,
                                          fechaPagoAnterior,
                                          currentPayment,
                                          Number(data.tasaInvestor)
                                        )
                                      )
                                    )
                                  : "Ingrese una fecha válida"}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {data?.status === "pagado" ? (
                  <div className="bg-primary/20 rounded-lg p-2">
                    <p>Este crédito ya fue pagado</p>
                    <div>
                      {" "}
                      Monto pagado al inversionista:{" "}
                      {formatCurrency(data?.montoPagoCompletoInvestor)}{" "}
                    </div>
                    <div> Fecha: {data?.fechaPagoCompleto} </div>
                  </div>
                ) : (
                  <div className="">
                    <div className="mt-4 ">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Progreso del crédito</span>
                        <span>{progressPercentage}% completado</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>
                          {pagosRealizados.length} de {data.month} pagos
                        </span>
                        <span>
                          {data.month - pagosRealizados.length} pagos restantes
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Left Column - Credit Info */}
          <div className="xl:col-span-2 space-y-6">
            {/* Credit Details Cards */}
            <div
              className={`transition-all duration-700 ease-out delay-200 ${
                isLoaded
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Loan Details */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Detalles del Préstamo
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Valor del préstamo:</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(Number(data?.ammount))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">
                        Tasa de interés anual del inversionista:
                      </span>
                      <span className="font-semibold text-blue-600">
                        {data.tasaInvestor}%{" "}
                        <button
                          onClick={() => setShowInvestorModal(true)}
                          className="bg-white/20 border border-1 hover:bg-white/30 backdrop-blur-sm px-1 py-1 rounded-lg text-sm font-medium transition-all duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="none"
                            viewBox="0 -0.5 25 25"
                          >
                            <path
                              fill="#000"
                              d="M13.294 7.959a.75.75 0 1 0-1.17-.938zm-6.483 6.89.568.49.017-.021zm-.171.418-.749-.05v.015zM6.5 18.29l-.75-.035a1 1 0 0 0 .004.112zm.787.687.025.75a1 1 0 0 0 .148-.02zm3-.711.173.73.012-.003zm.385-.244.579.477.006-.008zm6.625-7.063a.75.75 0 0 0-1.17-.938zm-5.17-3.938a.75.75 0 0 0 1.17.938zM14.3 5.51l.585.469.029-.038zm1.629-.32.48-.577a1 1 0 0 0-.075-.055zm2.237 1.862.53-.53-.05-.047zm.337.82.75.005zm-.346.818-.525-.536a1 1 0 0 0-.06.066zm-2.03 1.33a.75.75 0 1 0 1.17.94zM13.454 7.38a.75.75 0 1 0-1.484.222zm3.359 3.854a.75.75 0 1 0-.202-1.486zm-4.69-4.212L6.227 14.38l1.17.938 5.898-7.36zm-5.88 7.338c-.207.24-.33.542-.351.859l1.496.098a.04.04 0 0 1-.009.022zm-.352.873-.14 3.023 1.498.07.14-3.023zm-.137 3.135a1.516 1.516 0 0 0 1.558 1.36l-.05-1.5h-.005l-.006-.004-.003-.005-.002-.005zm1.706 1.34 3-.712-.346-1.459-3 .711zm3.012-.714c.305-.078.578-.25.779-.494l-1.158-.954.01-.006zm.785-.502 6.04-7.532-1.17-.938-6.04 7.532zm2.04-10.532 1.588-1.98-1.17-.938-1.588 1.98zm1.617-2.018a.45.45 0 0 1 .61-.12l.81-1.263a1.95 1.95 0 0 0-2.647.52zm.535-.175 2.237 1.862.96-1.153-2.237-1.862zm2.186 1.816c.076.076.118.179.118.287l1.5.008a1.9 1.9 0 0 0-.556-1.355zm.118.287c0 .107-.044.21-.121.285l1.05 1.071c.363-.355.568-.84.57-1.348zm-.18.351-1.446 1.8 1.17.94 1.445-1.8zM11.97 7.6a4.31 4.31 0 0 0 4.843 3.633l-.202-1.486a2.81 2.81 0 0 1-3.157-2.368z"
                            ></path>
                          </svg>
                        </button>
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Periodo en meses:</span>
                      <span className="font-semibold text-gray-900">
                        {data.month}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Fecha de inicio:</span>
                      <span className="font-semibold text-gray-900">
                        {data.start_date}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Investor Earnings */}
                <div
                  className={`transition-all duration-700 ease-out delay-500 ${
                    isLoaded
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
                >
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                            />
                          </svg>
                        </div>
                        <h2 className="text-xl font-semibold">Ganancias</h2>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                        <div className="mb-2">
                          <div className="text-sm opacity-90">
                            Cuota Mensual
                          </div>
                          <div className="text-lg font-bold">
                            {formatCurrency(monthlyInvestorEarning)}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Pagos realizados
                          </div>
                          <div className="text-xl font-bold">
                            {pagosRealizados.length}
                          </div>
                        </div>

                        <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Pagos realizados a la fecha
                          </div>
                          <div className="text-xl font-bold">
                            {formatCurrency(totalEarnedSoFar)}
                          </div>
                        </div>
                      </div>
                      {data?.status === "pagado" ? (
                        ""
                      ) : (
                        <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Deuda hasta la fecha
                          </div>
                          <div className="text-xl font-bold">
                            {formatCurrency(
                              currentPayment?.saldoDeuda.toFixed(0) || 0
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Schedule Table */}
        <div
          className={`transition-all duration-700 ease-out delay-600 ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Cronograma de Pagos
                  </h2>
                  <p className="text-sm text-gray-600">
                    Tabla detallada de amortización del crédito
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <TablePagos
                loading={loading || loadingInvestor}
                schedule={schedule}
                payment={idSimulator ? false : true}
                data={data}
                investor={true}
                investorData={investorData}
                disabled={data?.status === "pagado" ? true : false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Investor Rate Modal */}
      {showInvestorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Editar Tasa de Inversionista
              </h3>
              <button
                onClick={() => setShowInvestorModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tasa de ganancia anual (%)
                </label>
                <input
                  type="number"
                  value={investorRate}
                  onChange={(e) => setInvestorRate(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">
                  Vista previa de ganancias:
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ganancia mensual:</span>
                    <span className="font-medium">
                      {formatCurrency(monthlyInvestorEarning)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowInvestorModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleUpdateTasaInvestor()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-700 transition-all"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanDePagos;
